import { createDismissedState } from './createDismissedState';
import { DISMISS_STATE_KIND } from './DISMISS_STATE_KIND';
import type { DismissStateTransition } from './DismissStateTransition';
import { getLocalState } from './getLocalState';
import { reportUnexpectedDismissState } from './reportUnexpectedDismissState';
import { setLocalState } from './setLocalState';

export const recordDismissed = async (aaid: string): Promise<DismissStateTransition> => {
	const state = getLocalState(aaid);
	const now = Date.now();

	if (state === null) {
		// If Connect is clicked before first view or Sources Dropdown events are recorded,
		// write DISMISSED with firstViewedAt and dismissedAt set to now.
		await setLocalState(aaid, createDismissedState(now, now));
		return { from: null, to: DISMISS_STATE_KIND.DISMISSED, noop: false };
	}

	switch (state.kind) {
		case DISMISS_STATE_KIND.DISMISSED:
			// Already dismissed; keep the original dismissal time and do not rewrite it.
			return {
				from: DISMISS_STATE_KIND.DISMISSED,
				to: DISMISS_STATE_KIND.DISMISSED,
				noop: true,
			};

		case DISMISS_STATE_KIND.TIMER_RUNNING:
			// User opened Sources Dropdown earlier, then clicked Connect:
			// keep firstViewedAt and sourcesClickedAt, and add dismissedAt now.
			await setLocalState(
				aaid,
				createDismissedState(state.firstViewedAt, now, state.sourcesClickedAt),
			);
			return {
				from: DISMISS_STATE_KIND.TIMER_RUNNING,
				to: DISMISS_STATE_KIND.DISMISSED,
				noop: false,
			};

		case DISMISS_STATE_KIND.SEEN:
			// User clicked Connect before opening the Sources Dropdown, so we treat this as final dismissal.
			// Keep the original firstViewedAt time so the state history stays accurate.
			await setLocalState(aaid, createDismissedState(state.firstViewedAt, now));
			return { from: DISMISS_STATE_KIND.SEEN, to: DISMISS_STATE_KIND.DISMISSED, noop: false };

		default:
			reportUnexpectedDismissState({
				functionName: 'recordDismissed',
				kind: (state as { kind?: string }).kind,
			});
			return { from: null, to: null, noop: true };
	}
};
